import getActiveComp from 'common/src/modules/creativeTemplates/copilot/agents/adBuilderAgent/utils/getActiveComp';
import {setActiveLayerIds} from 'common/src/modules/creativeTemplates/store/creativeTemplatePage/formatMovies/formatMovies.slice';
import {ToolExecuteParams} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import {AdBuilderAgentToolName} from '../adBuilderAgent.enums';

// Schema for selecting layers
const inputSchema = z.object({
  layerIds: z.array(z.string()).describe('IDs to select. Empty array to clear selection.'),
});

function selectLayersTool(store: CLStore) {
  return tool({
    name: AdBuilderAgentToolName.SelectLayers,
    displayName: 'Selecting layers',
    description:
      'Selects or deselects layers in the composition. Use empty array to clear selection.',
    inputSchema,
    execute: async ({input}: ToolExecuteParams<typeof inputSchema>) => {
      const activeComp = getActiveComp(store);

      if (!activeComp) {
        return toolError('No active composition found');
      }

      try {
        // If no layer IDs provided, clear selection
        if (input.layerIds.length === 0) {
          store.dispatch(setActiveLayerIds([]));

          return toolResult('Cleared layer selection');
        }

        // Validate that all provided layer IDs exist in the composition
        const validLayerIds = activeComp.l.map((l) => l.id);
        const invalidLayerIds = input.layerIds.filter((id) => !validLayerIds.includes(id));

        if (invalidLayerIds.length > 0) {
          return toolError(
            `The following layer IDs do not exist in the composition: ${invalidLayerIds.join(', ')}`,
          );
        }

        // Update the layer selection
        store.dispatch(setActiveLayerIds(input.layerIds));

        // Build success message with layer names
        const selectedLayerNames = input.layerIds
          .map((id) => {
            const selectedLayer = activeComp.l.find((l) => l.id === id);

            return selectedLayer ? selectedLayer.name : id;
          })
          .join(', ');

        return toolResult(`Selected layers: ${selectedLayerNames}`);
      } catch (error) {
        return toolError(
          `Failed to select layers: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      }
    },
  });
}

export default selectLayersTool;
